/* eslint-disable max-lines */
import _ from 'lodash';
import { useState, useEffect } from 'react';

import validator from 'validator';
import QueryString from 'query-string';
import Text, { ErrorDescription } from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import { Checkbox, FormControlLabel, Switch } from '@material-ui/core';
import { isValidCertificate } from './utils';
import {
  AUTH_LEVEL,
  LOCAL_STORAGE_KEYS,
  SECURITY_PROVIDERS
} from 'common/constants/constants';
import { generateSAMLLoginUrl, omitFromQs } from 'common/utils/utils';
import {
  FEATURE_FLAGS_NAMES,
  SENSOR_NAMES,
  SENSOR_STATES
} from '@linearb/linjs-common';
import { getSamlSensorAuthorization } from 'state/selectors/sensors-authorizations';
import { useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import SingleSignOnState from './SingleSignOnState';
import SingleSignOnSetup from './SingleSignOnSetup';
import { createValidatingSamlSSOConfigration } from 'modals/modal-options-factory';
import Icon from 'components/base/Icon';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { SSOConfirmationDialog } from './SSOConfirmationDialog';
import { API_GATEWAY_URI } from 'common/constants/api-urls';
import copy from 'copy-to-clipboard';
import useSettingsService from 'components/hooks/useSettingsService';
import emailProviderDomains from 'common/constants/emailProviderDomains';

import {
  StyledTextWrapper,
  StyledTextFieldWrapper,
  StyledTextField,
  StyledInputWrappers
} from './SingleSignOn.style';
import {
  LBButton,
  LBLabel,
  LBRadioGroup,
  LBRadioGroupItem
} from '@linearb/buzz';

const SENSOR_AUTHORIZATION_ENTRY_POINT_KEY = 'entry_point';
const SENSOR_AUTHORIZATION_ISSUER_KEY = 'issuer';
const SENSOR_AUTHORIZATION_ORGANIZATION_DOMAIN =
  'profile.organization_email_domain';

const getTextField = ({
  title,
  error,
  icon,
  isInputDisplay = true,
  ...rest
}) => {
  const styledTextField = (
    <StyledTextField
      InputProps={{
        disableUnderline: true
      }}
      {...rest}
    />
  );
  return (
    <Padding t='small'>
      <Text size='medium' pt='0.2rem' color='shuttleGray'>
        {title}
      </Text>
      <Flex items='center'>
        {isInputDisplay ? (
          <StyledTextFieldWrapper>{styledTextField}</StyledTextFieldWrapper>
        ) : (
          <StyledTextWrapper>{styledTextField}</StyledTextWrapper>
        )}
        {icon}
      </Flex>
      <ErrorDescription minHeight='1.7rem'>{error}</ErrorDescription>
    </Padding>
  );
};
const _toggleSnackbar = ({ message, variant, toggleSnackBar }) => {
  toggleSnackBar({
    message: (
      <Flex direction='column'>
        <Text size='large' color='white'>
          {message}
        </Text>
      </Flex>
    ),
    variant
  });
};

// eslint-disable-next-line max-lines-per-function
const SingleSignOn = ({
  updateSensorAuthorization,
  createSensorAuthorization,
  toggleSnackBar,
  toggleModal
}) => {
  const samlSensorAuthorization = useSelector(getSamlSensorAuthorization);
  const [providerSignonUrl, setProviderSignonUrl] = useState(
    _.get(samlSensorAuthorization, SENSOR_AUTHORIZATION_ENTRY_POINT_KEY)
  );

  const [providerIssuer, setProviderIssuer] = useState(
    _.get(samlSensorAuthorization, SENSOR_AUTHORIZATION_ISSUER_KEY)
  );
  const [organizationEmailDomain, setOrganizationEmailDomain] = useState(
    _.get(samlSensorAuthorization, SENSOR_AUTHORIZATION_ORGANIZATION_DOMAIN)
  );
  const [certificate, setCertificate] = useState();

  const isSamlSensorAuthorization =
    samlSensorAuthorization && samlSensorAuthorization.id !== null;
  const isCertificationHidden =
    isSamlSensorAuthorization &&
    (certificate === undefined || certificate?.trim() === '');

  const myAccount = useSelector(myAccountSelector);
  const [canEdit, setCanEdit] = useState(
    samlSensorAuthorization?.state !== SENSOR_STATES.ok
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showEnableConfirmation, setShowEnableConfirmation] = useState(false);
  const [showSetup, setShowSetup] = useState(!isSamlSensorAuthorization);
  const [isSamlSSOActive, setIsSamlSSOActive] = useState(
    samlSensorAuthorization?.state !== SENSOR_STATES.inactive
  );
  const [requireUsersChecked, setRequireUsersChecked] = useState(
    samlSensorAuthorization?.profile?.forceSSO || false
  );
  const [rbacAllTeamsScope, setRbacAllTeamsScope] = useState(
    samlSensorAuthorization?.profile?.shouldProvisionRbacAllTeams ?? true
  );
  const { getSettingOrDefinitionValue } = useSettingsService();

  const createOrUpdateSensorAuthorization = async () => {
    toggleModal();
    const { email } = myAccount;

    const localStorageAccessor = BrowserStorageAccessor().local;

    try {
      const payload = {
        state: SENSOR_STATES.inProgress,
        ...(isCertificationHidden
          ? {}
          : {
              token: {
                token: certificate
              }
            }),
        profile: {
          entryPoint: providerSignonUrl,
          issuer: providerIssuer,
          organization_email_domain: organizationEmailDomain,
          admin_email: email,
          forceSSO: requireUsersChecked,
          shouldProvisionRbacAllTeams: rbacAllTeamsScope,
          ...(samlSensorAuthorization?.profile &&
          'disableRequestedAuthnContext' in samlSensorAuthorization.profile
            ? {
                disableRequestedAuthnContext:
                  samlSensorAuthorization.profile.disableRequestedAuthnContext
              }
            : {})
        },
        sensor_id: SECURITY_PROVIDERS.SAML,
        authorization_level: AUTH_LEVEL.ORGANIZATION,
        name: SENSOR_NAMES.SAML_SSO
      };

      let result;
      if (isSamlSensorAuthorization) {
        result = await updateSensorAuthorization(
          samlSensorAuthorization.id,
          payload
        );
      } else {
        result = await createSensorAuthorization(payload);
      }

      if (!result) throw new Error();

      localStorageAccessor.set(LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY, true);
      // Use a small delay to ensure the value is set before redirecting
      setTimeout(() => {
        window.location.href = generateSAMLLoginUrl(
          organizationEmailDomain,
          localStorageAccessor.get(LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY)
        );
      }, 100); // Delay of 100 milliseconds

      _toggleSnackbar({
        message: 'Configuration was saved successfully',
        variant: 'success',
        toggleSnackBar
      });
    } catch (exp) {
      localStorageAccessor.remove(LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY);
      _toggleSnackbar({
        message: 'There was an issue saving configuration',
        variant: 'error',
        toggleSnackBar
      });
    }
  };

  const validateConfiguration = () => {
    toggleModal(
      createValidatingSamlSSOConfigration({
        onSaveSSO: createOrUpdateSensorAuthorization,
        configuration: {
          entryPoint: providerSignonUrl
        },
        onEditClicked: () => {
          toggleModal();
        }
      })
    );
  };

  const generateCallbackURL = () =>
    `${API_GATEWAY_URI}/api/v1/rest/public/auth/identity/proxy/saml/${myAccount.organization_id}`;

  const canValidateUrl = () => {
    try {
      const { protocol = '' } = new URL(providerSignonUrl?.trim());
      return (
        providerSignonUrl?.trim() !== '' &&
        protocol.toLowerCase() === 'https:' &&
        validator.isURL(providerSignonUrl)
      );
    } catch (e) {
      return false;
    }
  };

  const isProviderIssuerValid = providerIssuer?.trim() !== '';
  const isOrganizationEmailDomainValid =
    _.isString(organizationEmailDomain) &&
    organizationEmailDomain?.trim() !== '' &&
    validator.isFQDN(organizationEmailDomain) &&
    !emailProviderDomains.includes(organizationEmailDomain);

  const forceSSOLoginFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.forceSSOLogin
  });
  const isRBACEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isRBACEnabled
  });

  const isCertificateValid =
    isCertificationHidden ||
    (certificate?.trim() !== '' && isValidCertificate(certificate));

  const isProviderSignonUrlValid = canValidateUrl();

  const canValidate = () => {
    return (
      isProviderSignonUrlValid &&
      isProviderIssuerValid &&
      isCertificateValid &&
      isOrganizationEmailDomainValid
    );
  };

  const onSwitch = event => {
    if (event.target.checked) {
      setCanEdit(true);
    }
    if (event.target.checked) {
      setShowEnableConfirmation(true);
      return;
    } else if (
      !event.target.checked &&
      samlSensorAuthorization?.state === SENSOR_STATES.ok
    ) {
      setShowConfirmation(true);
      return;
    }
    ssoActivationToggle(event.target.checked);
  };

  const ssoActivationToggle = async isActive => {
    if (isActive) {
      validateConfiguration();
      setIsSamlSSOActive(isActive);
    } else {
      try {
        const payload = {
          state: SENSOR_STATES.inactive
        };

        let result;
        if (isSamlSensorAuthorization) {
          result = await updateSensorAuthorization(
            samlSensorAuthorization.id,
            payload
          );
        }

        if (!result) throw new Error();

        setIsSamlSSOActive(isActive);
      } catch (exp) {
        _toggleSnackbar({
          message: 'There was an issue saving configuration',
          variant: 'error',
          toggleSnackBar
        });
      }
    }
  };

  const onSetupClicked = () => {
    setShowSetup(false);
    setCanEdit(true);
  };

  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });

  useEffect(() => {
    const { test_connectivity_res } = search;
    omitFromQs('test_connectivity_res');
    if (test_connectivity_res) {
      toggleModal(
        createValidatingSamlSSOConfigration({
          testConnectivityRes: test_connectivity_res,
          onEditClicked: () => toggleModal()
        })
      );
    }
  }, [window.location.search]);

  return (
    <>
      <Padding t='x-medium'>
        <SingleSignOnState
          isSamlSSOActive={isSamlSSOActive}
          sensorState={samlSensorAuthorization?.state}
        />
        {showSetup ? (
          <SingleSignOnSetup onSetupClicked={onSetupClicked} />
        ) : (
          <StyledInputWrappers>
            <Text size='large' color='shuttleGray' pb='1rem'>
              Allow SAML authentication through identity providers
            </Text>
            {isSamlSensorAuthorization && (
              <Padding t='small' b='small'>
                <FormControlLabel
                  control={
                    <Switch checked={isSamlSSOActive} onChange={onSwitch} />
                  }
                  label={
                    <Text textTransform='uppercase' size='medium'>
                      saml authentication: {isSamlSSOActive ? 'on' : 'off'}
                    </Text>
                  }
                />
              </Padding>
            )}
            {isSamlSSOActive && (
              <>
                {getTextField({
                  title: 'Callback url',
                  value: generateCallbackURL(),
                  disabled: true,
                  isInputDisplay: false,
                  icon: (
                    <LBButton
                      variant='link'
                      size='icon'
                      ml='negative-nomral'
                      onClick={() => copy(generateCallbackURL())}
                    >
                      <Icon name='Clipboard' />
                    </LBButton>
                  )
                })}
                {getTextField({
                  title: 'Provider single sign-on url',
                  error:
                    isProviderSignonUrlValid || !providerSignonUrl
                      ? null
                      : 'Invalid single sign-on URL',
                  placeholder:
                    'Enter your SAML endpoint - this is where users will navigate to log in',
                  value: providerSignonUrl,
                  disabled: !canEdit,
                  onChange: event => setProviderSignonUrl(event.target.value)
                })}
                {getTextField({
                  title: 'Provider issuer',
                  error: isProviderIssuerValid
                    ? null
                    : 'Provider Issuer is mandatory',
                  placeholder: 'The IDP entity ID for the service you use',
                  value: providerIssuer,
                  disabled: !canEdit,
                  onChange: event => setProviderIssuer(event.target.value)
                })}
                {getTextField({
                  title: 'Organization email domain',
                  error:
                    isOrganizationEmailDomainValid || !organizationEmailDomain
                      ? null
                      : 'Invalid organization email domain',
                  placeholder: 'Enter organization email domain',
                  value: organizationEmailDomain,
                  disabled: !canEdit,
                  onChange: event =>
                    setOrganizationEmailDomain(event.target.value)
                })}
                {getTextField({
                  title: 'Certificate',
                  error:
                    isCertificateValid || !certificate
                      ? null
                      : 'Invalid Certificate',
                  placeholder: isSamlSensorAuthorization
                    ? 'Certificate hidden'
                    : 'Copy and paste your x.509 certificate text here',
                  multiline: true,
                  minRows: 10,
                  maxRows: 10,
                  value: certificate,
                  disabled: !canEdit,
                  onChange: event => setCertificate(event.target.value)
                })}

                {forceSSOLoginFF && (
                  <Padding t='small' b='small'>
                    <FormControlLabel
                      control={
                        <Checkbox
                          data-testid='sso-force'
                          disabled={!canEdit}
                          checked={requireUsersChecked}
                          onChange={event => {
                            setRequireUsersChecked(event.target.checked);
                          }}
                        />
                      }
                      label={
                        <Text disabled={!canEdit} size='medium'>
                          Team members will only be able to access LinearB using
                          your SSO provider
                        </Text>
                      }
                    />
                  </Padding>
                )}
                {isRBACEnabledFF && (
                  <Padding t='small' b='small'>
                    <LBRadioGroup
                      data-testid='sso-toggle-rbac-scope'
                      value={rbacAllTeamsScope.toString()}
                      onValueChange={value =>
                        setRbacAllTeamsScope(value === 'true')
                      }
                    >
                      <div className='flex flex-row gap-4'>
                        <LBRadioGroupItem
                          id='true'
                          value='true'
                          disabled={!canEdit}
                        />
                        <LBLabel htmlFor='true'>
                          <Text disabled={!canEdit} size='medium'>
                            When users first log in, they get access to all
                            teams (access can be scoped down by an admin)
                          </Text>
                        </LBLabel>
                      </div>
                      <div className='flex flex-row gap-4'>
                        <LBRadioGroupItem
                          id='false'
                          value='false'
                          disabled={!canEdit}
                        />
                        <LBLabel htmlFor='false'>
                          <Text disabled={!canEdit} size='medium'>
                            When users first log in, they don’t get access to
                            any teams. Users will be prompted to ask an admin
                            for access.
                          </Text>
                        </LBLabel>
                      </div>
                    </LBRadioGroup>
                  </Padding>
                )}
                <Flex pt='small' pb='small'>
                  {!canEdit ? (
                    <LBButton
                      data-testid='edit-sso-button'
                      onClick={() => setCanEdit(true)}
                    >
                      <Icon
                        name='Pencil'
                        color='silverSand'
                        style={{ marginRight: '0.3rem' }}
                      />
                      Edit
                    </LBButton>
                  ) : (
                    <LBButton
                      onClick={validateConfiguration}
                      disabled={!canValidate() || !canEdit}
                    >
                      Save
                    </LBButton>
                  )}
                </Flex>
              </>
            )}
          </StyledInputWrappers>
        )}
      </Padding>
      {showConfirmation && (
        <SSOConfirmationDialog
          type='revoke'
          isOpenState={showConfirmation}
          isOpenSetState={setShowConfirmation}
          onConfirm={() => ssoActivationToggle(false)}
        />
      )}
      {showEnableConfirmation && (
        <SSOConfirmationDialog
          type='enable'
          isOpenState={showEnableConfirmation}
          isOpenSetState={setShowEnableConfirmation}
          onConfirm={() => ssoActivationToggle(true)}
        />
      )}
    </>
  );
};

export default SingleSignOn;
